/**
 * InvoiceFiltersBar Component
 * Filter controls for invoices (desktop view)
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Filter, X, SlidersHorizontal, LayoutGrid, List, Loader2 } from 'lucide-react';
import type { InvoiceFilters } from '../types/invoice.types';
import type { InvoiceStatus } from '@/types/invoice';
import { useAvailableInvoiceStatuses } from '../hooks/useAvailableInvoiceStatuses';

// ============================================
// STATUS CONFIGURATION
// ============================================

/**
 * Detailed invoice status configuration with proper labels and colors
 * Maps each status to its display name and styling
 */
const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'bg-slate-100 text-slate-800' },
  ISSUED: { label: 'Issued', color: 'bg-slate-100 text-slate-800' },
  SENT: { label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'bg-orange-100 text-orange-800' },
  PAID: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800' },
  OVERDUE: { label: 'Overdue', color: 'bg-red-100 text-red-800' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-200 text-red-900' },
  VOID: { label: 'Void', color: 'bg-gray-100 text-gray-800' },
};

const SORT_OPTIONS = [
  { value: 'invoiceDate-desc', label: 'Invoice Date (Newest First)' },
  { value: 'invoiceDate-asc', label: 'Invoice Date (Oldest First)' },
  { value: 'totalAmount-desc', label: 'Amount (High to Low)' },
  { value: 'totalAmount-asc', label: 'Amount (Low to High)' },
  { value: 'dueDate-asc', label: 'Due Date (Earliest First)' },
  { value: 'dueDate-desc', label: 'Due Date (Latest First)' },
];

// ============================================
// COMPONENT PROPS
// ============================================

export interface InvoiceFiltersBarProps {
  filters: InvoiceFilters;
  onSearchChange: (searchTerm: string) => void;
  onStatusToggle: (status: InvoiceStatus) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onClearFilters: () => void;
  onOpenMobileFilters?: () => void;
  activeFilterCount: number;
  onSortChange?: (sortBy: string, sortDirection: 'asc' | 'desc') => void;
  viewMode?: 'list' | 'table';
  onViewModeChange?: (mode: 'list' | 'table') => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * InvoiceFiltersBar Component
 * Desktop filter controls with search, status filters, and date range
 *
 * @example
 * ```tsx
 * <InvoiceFiltersBar
 *   filters={filters}
 *   onSearchChange={setSearchTerm}
 *   onStatusToggle={toggleStatus}
 *   onDateRangeChange={setDateRange}
 *   onClearFilters={clearFilters}
 *   activeFilterCount={2}
 * />
 * ```
 */
export function InvoiceFiltersBar({
  filters,
  onSearchChange,
  onStatusToggle,
  onDateRangeChange,
  onClearFilters,
  onOpenMobileFilters,
  activeFilterCount,
  onSortChange,
  viewMode = 'list',
  onViewModeChange,
}: InvoiceFiltersBarProps) {
  const [searchInput, setSearchInput] = useState(filters.searchTerm || '');
  const [dateFromInput, setDateFromInput] = useState(filters.dateFrom || '');
  const [dateToInput, setDateToInput] = useState(filters.dateTo || '');

  // Fetch available statuses based on current filters
  const { availableStatuses, isLoading: isLoadingStatuses } = useAvailableInvoiceStatuses(
    searchInput,
    dateFromInput,
    dateToInput
  );

  // Current sort value
  const currentSort = `${filters.sortBy || 'invoiceDate'}-${filters.sortDirection || 'desc'}`;

  const handleSortChange = (value: string) => {
    if (onSortChange) {
      const [field, direction] = value.split('-') as [string, 'asc' | 'desc'];
      onSortChange(field, direction);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    onSearchChange(value);
  };

  const handleDateFromChange = (value: string) => {
    setDateFromInput(value);
    onDateRangeChange(value, dateToInput);
  };

  const handleDateToChange = (value: string) => {
    setDateToInput(value);
    onDateRangeChange(dateFromInput, value);
  };

  const selectedStatuses = filters.statuses || [];

  return (
    <div className="space-y-4">
      {/* Desktop View */}
      <div className="hidden md:flex items-center gap-3 flex-wrap">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[250px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by invoice number..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Date From Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 whitespace-nowrap">From:</label>
          <Input
            type="date"
            value={dateFromInput}
            onChange={(e) => handleDateFromChange(e.target.value)}
            className="w-[150px]"
          />
        </div>

        {/* Date To Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 whitespace-nowrap">To:</label>
          <Input
            type="date"
            value={dateToInput}
            onChange={(e) => handleDateToChange(e.target.value)}
            className="w-[150px]"
          />
        </div>

        {/* Status Filter Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="default" disabled={isLoadingStatuses}>
              <Filter className="h-4 w-4 mr-2" />
              Status
              {isLoadingStatuses && (
                <Loader2 className="h-3.5 w-3.5 ml-2 animate-spin" />
              )}
              {selectedStatuses.length > 0 && !isLoadingStatuses && (
                <Badge variant="secondary" className="ml-2">
                  {selectedStatuses.length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-[280px] overflow-y-auto">
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isLoadingStatuses ? (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading statuses...
              </div>
            ) : availableStatuses.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No statuses available
              </div>
            ) : (
              availableStatuses.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selectedStatuses.includes(status)}
                  onCheckedChange={() => onStatusToggle(status)}
                >
                  <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_CONFIG[status].color}`}>
                    {STATUS_CONFIG[status].label}
                  </span>
                </DropdownMenuCheckboxItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sort Dropdown */}
        {onSortChange && (
          <Select value={currentSort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[200px]" aria-label="Sort invoices">
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* View Mode Toggle */}
        {onViewModeChange && (
          <div className="flex items-center border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('list')}
              className="rounded-none"
              aria-label="List view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('table')}
              className="rounded-none"
              aria-label="Table view"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Clear Filters Button */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="default"
            onClick={onClearFilters}
            className="text-slate-600 hover:text-slate-900"
          >
            <X className="h-4 w-4 mr-2" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-3">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search invoices..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Mobile Filter Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="default"
            onClick={onOpenMobileFilters}
            className="flex-1"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClearFilters}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Active Filter Tags */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">Active filters:</span>
          {selectedStatuses.map((status) => (
            <Badge
              key={status}
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => onStatusToggle(status)}
            >
              {STATUS_CONFIG[status].label}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          ))}
          {dateFromInput && (
            <Badge
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => handleDateFromChange('')}
            >
              From: {new Date(dateFromInput).toLocaleDateString()}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
          {dateToInput && (
            <Badge
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => handleDateToChange('')}
            >
              To: {new Date(dateToInput).toLocaleDateString()}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default InvoiceFiltersBar;
